import {
  Ellipsis,
  FileCode2,
  FilePlusIcon,
  PencilIcon,
  PrinterIcon,
  MailCheckIcon,
  MailXIcon,
  SendIcon,
  WebhookIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'

import { displayStatus, relativeDue, statusLabelFor } from '@/components/documents/documentUtils'
import { KeyValueList, Money, StatusBadge, Timeline } from '@/components/ds'
import { MarkDocumentPaidForm } from '@/forms/MarkDocumentPaidForm/MarkDocumentPaidForm'
import { useRoleCheck } from '@/hooks/useRoleCheck'
import { useSubscriptionCheck } from '@/hooks/useSubscriptionCheck'
import { DocumentData } from '@/interfaces/DocumentData'
import { Invoice } from '@/interfaces/Invoice'
import API, { API_URL } from '@/lib/api'
import { formatDate } from '@/lib/formats/formatDate'
import { Role } from '@/types/User'

import { Button } from '../ui/button'
import { ButtonGroup } from '../ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { ParticipantDetail } from './ParticipantDetail'

export const DocumentDetail = ({
  document,
  linkSegment,
}: {
  document: DocumentData & Invoice
  linkSegment: 'invoice' | 'expense' | 'order'
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'documentDetail',
  })
  const { t: tp } = useTranslation(undefined, {
    keyPrefix: 'paymentMethod',
  })

  const totalWithTax = document.items?.reduce(
    (acc, item) => acc + Number(item.totalPrice.amount!) / 100,
    0,
  )

  const taxSummary = document.items?.reduce((acc, item) => {
    const taxRate = item.taxRate!
    const taxAmount =
      Number(item.totalPrice.amount!) / 100 -
      Number(item.totalPriceTaxFree.amount!) / 100
    acc[taxRate] = (acc[taxRate] || 0) + taxAmount
    return acc
  }, {} as Record<number, number>)

  const isVatPayer = document.supplier?.vatPayer === 'vatPayer'

  const webhookEvent =
    linkSegment === 'invoice'
      ? 'invoicePaid'
      : linkSegment === 'order'
        ? 'orderPaid'
        : null

  const isSia = useSubscriptionCheck()

  const triggerWebhook = async () => {
    if (!webhookEvent || !document.uuid) return
    try {
      const { data } = await API.post('/api/v2/webhooks', {
        event: webhookEvent,
        entityId: document.uuid,
      })
      if (data.delivered) {
        toast.success(t('actions.webhook.success'))
      } else if (data.reason === 'webhook_url_not_configured') {
        toast.warning(t('actions.webhook.notConfigured'))
      } else if (data.reason === 'webhook_unreachable') {
        toast.error(t('actions.webhook.unreachable'))
      } else if (data.reason === 'webhook_rejected' || data.remoteStatusCode) {
        toast.error(t('actions.webhook.rejected', { status: data.remoteStatusCode }))
      } else {
        toast.error(t('actions.webhook.error'))
      }
    } catch {
      toast.error(t('actions.webhook.error'))
    }
  }

  // Faktury a výzvy k platbě poslané mimo invo.space (vlastní e-mail, SMS) jde označit ručně.
  const canMarkSent = linkSegment !== 'expense' && document.status !== 'cancelled' && document.status !== 'refunded'
  const isSent = Boolean(document.sentAt)

  const toggleSent = async () => {
    if (!document.uuid) return
    try {
      if (isSent) {
        await API.delete(`/api/v2/sent-documents/${document.uuid}`, { data: {} })
      } else {
        await API.post(`/api/v2/sent-documents/${document.uuid}`, {})
      }
      toast.success(t(isSent ? 'actions.markNotSentSuccess' : 'actions.markSentSuccess'))
      window.location.reload()
    } catch {
      toast.error(t('actions.markSentError'))
    }
  }

  const status = displayStatus(document as never)
  const currency = document.currency ?? document.items?.[0]?.totalPrice?.currency ?? 'CZK'
  const canEdit = useRoleCheck([Role.owner, Role.manager, Role.employee])
  const due = document.paidAt ? null : relativeDue(document.deadlineAt)
  const kind = linkSegment
  const bankAccount = document.supplierBankAccount?.accountNumber
    ? `${document.supplierBankAccount.accountNumber}${document.supplierBankAccount.bankCode ? `/${document.supplierBankAccount.bankCode}` : ''}`
    : document.supplierBankAccount?.iban
      ? document.supplierBankAccount.iban
      : document.supplier?.accountNumber

  return (
    <div className="mx-auto grid w-full max-w-[1280px] gap-8">
      <header className="flex flex-wrap items-start justify-between gap-6 border-b pb-6">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-medium tracking-[-0.02em] tabular">{document.symbol}</h1>
            <StatusBadge status={status} label={statusLabelFor(kind, status)} />
          </div>
          <p className="text-sm text-muted-foreground">
            {kind === 'expense' ? document.supplier?.name : document.customer?.name}
          </p>
          <Money amount={totalWithTax !== undefined ? Math.round(totalWithTax * 100) : 0} currency={currency} size="xl" animate />
          <p className={due?.late ? 'text-sm text-danger' : 'text-sm text-muted-foreground'}>
            {document.paidAt
              ? t('paidAtSentence', { date: formatDate(document.paidAt!) })
              : due
                ? `Splatnost ${formatDate(document.deadlineAt!)}, ${due.text}`
                : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {useRoleCheck([Role.owner, Role.manager, Role.employee]) && (
            <DropdownMenu>
              {isSia ? (
                <ButtonGroup>
                  <Button variant="outline" asChild>
                    <Link
                      to={`/${linkSegment}/${document.symbol}/edit`}
                      className="flex items-center gap-2"
                    >
                      <PencilIcon className="w-4 h-4" />
                      <span>{t('actions.edit')}</span>
                    </Link>
                  </Button>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="px-1">
                      <Ellipsis className="size-3" />
                    </Button>
                  </DropdownMenuTrigger>
                </ButtonGroup>
              ) : (
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="px-1">
                    <Ellipsis className="size-3" />
                  </Button>
                </DropdownMenuTrigger>
              )}
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link
                    to={`/${linkSegment}/new?customerId=${document.customer?.id}`}
                    className="flex justify-between gap-2"
                  >
                    <FilePlusIcon className="w-4 h-4" />
                    <span>{t('actions.issueAgain')}</span>
                  </Link>
                </DropdownMenuItem>
                {canMarkSent && (
                  <DropdownMenuItem onClick={toggleSent} className="flex justify-between gap-2">
                    {isSent ? <MailXIcon className="w-4 h-4" /> : <MailCheckIcon className="w-4 h-4" />}
                    <span>{t(isSent ? 'actions.markNotSent' : 'actions.markSent')}</span>
                  </DropdownMenuItem>
                )}
                {document.paidAt && webhookEvent && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={triggerWebhook}
                      className="flex justify-between gap-2"
                    >
                      <WebhookIcon className="w-4 h-4" />
                      <span>{t('actions.webhook.triggerPaid')}</span>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <DropdownMenu>
            <ButtonGroup>
              <Button variant="outline" asChild>
                <Link
                  to={
                    API_URL +
                    `/api/v2/permalink/${linkSegment}/${document.uuid}`
                  }
                  target="_blank"
                >
                  <PrinterIcon className="w-4 h-4" />
                  <span>{t('actions.print')}</span>
                </Link>
              </Button>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="px-1">
                  <Ellipsis className="size-3" />
                </Button>
              </DropdownMenuTrigger>
            </ButtonGroup>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link
                  to={API_URL + `/api/v2/permalink/isdoc/${document.uuid}`}
                  target="_blank"
                  className="flex justify-between gap-2"
                >
                  <FileCode2 className="w-4 h-4" />
                  <span>{t('actions.isdoc')}</span>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {useRoleCheck([Role.owner, Role.manager, Role.employee]) && (
            <Link
              to={`/${linkSegment}/${document.symbol}/send`}
              className="flex items-center gap-2"
            >
              <Button variant="outline">
                <SendIcon className="w-4 h-4" />
                <span>{t('actions.send')}</span>
              </Button>
            </Link>
          )}
        </div>
      </header>

      {canEdit && document.paidAt === null && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-muted px-4 py-3">
          <p className="text-sm">Přišla platba? Označte doklad jako zaplacený.</p>
          <MarkDocumentPaidForm document={document} linkSegment={linkSegment} />
        </div>
      )}

      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="grid min-w-0 gap-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <ParticipantDetail participant={document.supplier!} title={t('supplier')} />
            <ParticipantDetail participant={document.customer!} title={t('customer')} />
          </div>

          {document.description && <p className="text-sm text-muted-foreground">{document.description}</p>}

          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('items.description')}</TableHead>
                  <TableHead className="hidden text-right md:table-cell">{t('items.quantity')}</TableHead>
                  <TableHead className="hidden text-right md:table-cell">{t('items.unitPrice')}</TableHead>
                  {isVatPayer && <TableHead className="hidden text-right md:table-cell">{t('items.taxRate')}</TableHead>}
                  {isVatPayer && <TableHead className="hidden text-right md:table-cell">{t('items.totalWithoutTax')}</TableHead>}
                  <TableHead className="text-right">{t(isVatPayer ? 'items.totalWithTax' : 'items.total')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {document.items?.map((item, i) => (
                  <TableRow key={i} className="hover:bg-transparent">
                    <TableCell className="whitespace-normal">
                      {item.description}
                      <p className="text-xs text-muted-foreground md:hidden">
                        {item.amount} {item.unit} ×{' '}
                        <Money amount={item.unitPriceTaxFree.amount} currency={item.unitPriceTaxFree.currency} className="font-normal" />
                        {isVatPayer && `, DPH ${item.taxRate} %`}
                      </p>
                    </TableCell>
                    <TableCell className="hidden text-right md:table-cell">
                      {item.amount} {item.unit}
                    </TableCell>
                    <TableCell className="hidden text-right md:table-cell">
                      <Money amount={item.unitPriceTaxFree.amount} currency={item.unitPriceTaxFree.currency} className="font-normal" />
                    </TableCell>
                    {isVatPayer && <TableCell className="hidden text-right md:table-cell">{item.taxRate} %</TableCell>}
                    {isVatPayer && (
                      <TableCell className="hidden text-right md:table-cell">
                        <Money amount={item.totalPriceTaxFree.amount} currency={item.totalPriceTaxFree.currency} className="font-normal" />
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <Money amount={item.totalPrice.amount} currency={item.totalPrice.currency} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="ml-auto grid max-w-sm gap-2 border-t px-5 py-4 text-sm">
              {isVatPayer &&
                taxSummary &&
                Object.entries(taxSummary).map(([taxRate, taxAmount]) => (
                  <div key={taxRate} className="flex justify-between text-muted-foreground">
                    <span>DPH {taxRate} %</span>
                    <Money amount={Math.round(taxAmount * 100)} currency={currency} className="font-normal text-foreground" />
                  </div>
                ))}
              <div className="flex items-baseline justify-between border-t pt-2">
                <span className="font-medium">Celkem</span>
                <Money amount={Math.round((totalWithTax ?? 0) * 100)} currency={currency} size="lg" />
              </div>
            </div>
          </div>
        </div>

        <aside className="grid gap-6">
          <KeyValueList
            items={[
              { label: t('issuedAt'), value: formatDate(document.issuedAt!) },
              ...(document.taxableAt ? [{ label: 'DUZP', value: formatDate(document.taxableAt!) }] : []),
              { label: t('deadlineAt'), value: formatDate(document.deadlineAt!) },
              { label: t('paymentMethod'), value: tp(document.paymentMethod!) },
              ...(bankAccount ? [{ label: t('bankAccount'), value: bankAccount }] : []),
              { label: t('variableSymbol'), value: document.vs || document.symbol },
            ]}
          />
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Historie</p>
            <Timeline
              events={[
                { label: 'Vytvořeno', date: document.createdAt ? formatDate(document.createdAt) : undefined },
                { label: 'Vystaveno', date: formatDate(document.issuedAt!) },
                ...(document.sentAt ? [{ label: 'Odesláno', date: formatDate(document.sentAt) }] : []),
                document.paidAt
                  ? { label: 'Zaplaceno', date: formatDate(document.paidAt) }
                  : { label: due?.late ? 'Po splatnosti' : 'Čeká na platbu', date: formatDate(document.deadlineAt!), done: false },
              ]}
            />
          </div>
        </aside>
      </div>
    </div>
  )
}
